/**
 * Экран ревью `/draft` (§6.3) — место, где черновик становится пакетом.
 *
 * Это отдельная страница, а не модалка: её можно перезагрузить, открыть в
 * новой вкладке и скинуть себе. Центральная колонка 760 px.
 *
 * Решения спеки, которые видно в коде:
 *  · порядок блоков: конфликты → новые бренды → бренды по алфавиту → массовые
 *    правки → архивация и отвязки (§6.3), тот же порядок в Telegram (§6.5);
 *  · пока не решены все конфликты, публикация заблокирована, и причину
 *    объясняет сама кнопка — не тост и не красный текст (§6.4);
 *  · `beforeunload` не используется (§6.6): черновик на сервере, терять нечего;
 *  · ответ `200` с `appliedCount: 0` — это «публиковать было нечего», а не
 *    ошибка: все спорные правки человек оставил за коллегами (§6.4).
 *
 * Публикация идёт через `api.commit`, а не через `commit()` стора: экрану нужны
 * номер пакета, `appliedCount` и статус Telegram из ответа, а стор возвращает
 * только признак успеха. Данные после публикации перечитываются действиями
 * стора, поэтому состояние приложения остаётся согласованным (см. отчёт).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { Change, ChannelCode } from '@shared/types.ts'
import {
  Button, ConsequenceLine, EmptyState, IconArrowLeft, IconWarning, Textarea,
} from '../../components/index.js'
import { ApiRequestError, api } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { texts } from '../../texts.js'
import { ChangeGroup, OperationLine, buildReviewGroups, taskDonePairs } from './ChangeGroup.js'
import { ChangeLine, brandNameOf, changeTitle, isValuePair, valueText, type ReviewContext } from './ChangeLine.js'
import { ConflictResolver, type ConflictDecision, type ConflictDecisions } from './ConflictResolver.js'
import { DiscardDraftModal } from './DraftDock.js'
import { TelegramPreview } from './TelegramPreview.js'
import { daysSince, draftTexts, formatMoment } from './texts.draft.js'
import './DraftReviewScreen.css'

/** Старше этого — при входе показываем плашку «часть значений могла измениться» (§6.6). */
const STALE_DAYS = 14

/**
 * Событие для волны таблицы: опубликованные ячейки должны вспыхнуть
 * `--success-bg` на 1200 ms (§6.5). Вспышку рисует таблица, здесь — сигнал.
 */
export interface PublishedEventDetail {
  changeSetId: number
  appliedCount: number
  brandIds: number[]
}

export interface DraftReviewScreenProps {
  /**
   * Ревью показано окном поверх экрана, а не отдельной страницей. Тогда у него
   * нет своего заголовка и ссылки «назад»: их даёт окно, а по закрытии человек
   * остаётся ровно там, где был, — в таблице на том же месте или в карточке
   * бренда на той же вкладке.
   */
  embedded?: boolean
  /** Закрыть ревью: вместо перехода на /brands. */
  onClose?: () => void
}

export function DraftReviewScreen({ embedded = false, onClose }: DraftReviewScreenProps = {}) {
  const navigate = useNavigate()
  /** Куда уходить после публикации и по «назад». */
  const leave = onClose ?? (() => navigate('/brands'))

  const bootstrap = useApp((s) => s.bootstrap)
  const changes = useApp((s) => s.draftChanges)
  const conflicts = useApp((s) => s.conflicts)
  const brandsById = useApp((s) => s.brandsById)
  const storedNote = useApp((s) => s.draftNote)
  const setDraftNote = useApp((s) => s.setDraftNote)
  const dropChange = useApp((s) => s.dropChange)
  const reloadBrands = useApp((s) => s.reloadBrands)
  const reloadDraft = useApp((s) => s.reloadDraft)
  const editBrand = useApp((s) => s.editBrand)
  const editChannel = useApp((s) => s.editChannel)
  const editCategoryChannel = useApp((s) => s.editCategoryChannel)
  const toast = useApp((s) => s.toast)

  const [decisions, setDecisions] = useState<ConflictDecisions>({})
  const [publishing, setPublishing] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [undone, setUndone] = useState<Change | null>(null)
  const [noteDraft, setNoteDraft] = useState(storedNote)
  const noteTouched = useRef(false)
  const listRef = useRef<HTMLDivElement | null>(null)

  /* Черновик один на все устройства: открывая ревью, перечитываем его и
     конфликты — экран могли открыть ссылкой спустя часы (§6.6). */
  useEffect(() => { void reloadDraft() }, [reloadDraft])

  useEffect(() => { if (!noteTouched.current) setNoteDraft(storedNote) }, [storedNote])

  const changesById = useMemo(
    () => new Map(changes.map((change) => [change.id, change])),
    [changes],
  )

  const channelNames = useMemo(() => {
    const map = new Map<ChannelCode, string>()
    for (const channel of bootstrap?.channels ?? []) map.set(channel.code, channel.name)
    return map
  }, [bootstrap])

  const touchedChannels = useMemo(() => {
    const set = new Set<ChannelCode>()
    for (const change of changes) if (change.key.channelCode) set.add(change.key.channelCode)
    return [...set]
  }, [changes])

  const ctx: ReviewContext = useMemo(
    () => ({ brandsById, channelNames, showChannel: touchedChannels.length > 1 }),
    [brandsById, channelNames, touchedChannels],
  )

  const groups = useMemo(() => buildReviewGroups(changes, ctx), [changes, ctx])

  const brandsCount = useMemo(
    () => new Set(changes.map((change) => change.brandId).filter((id): id is number => id !== null)).size,
    [changes],
  )

  const conflictIds = useMemo(
    () => new Set(conflicts.filter((c) => changesById.has(c.changeId)).map((c) => c.changeId)),
    [conflicts, changesById],
  )
  const unresolved = [...conflictIds].filter((id) => decisions[id] === undefined).length

  const startedAt = changes.reduce<string | null>(
    (first, change) => (first === null || change.createdAt < first ? change.createdAt : first),
    null,
  )
  const age = daysSince(startedAt)

  const firstChannel = touchedChannels[0]
  const channelTitle = touchedChannels.length > 1
    ? draftTexts.review.bothChannels
    : firstChannel ? channelNames.get(firstChannel) ?? null : null

  /* Отчёт о выполнении не считается отдельной правкой: человек сделал одно
     движение, и в шапке ревью должно стоять то же число, что он ожидает. */
  const visibleCount = changes.length - taskDonePairs(changes).hidden.size

  const subtitle = [
    draftTexts.review.changes(visibleCount),
    draftTexts.review.brands(brandsCount),
    channelTitle,
    startedAt ? draftTexts.review.startedAt(formatMoment(startedAt) ?? '') : null,
  ].filter((part): part is string => Boolean(part)).join(' · ')

  // ── убрать правку и вернуть её обратно (§6.3) ──────────────────────────────

  const removeChanges = useCallback(async (ids: number[], restorable: Change | null) => {
    for (const id of ids) await dropChange(id)
    setUndone(restorable && restorable.action === 'update' ? restorable : null)
  }, [dropChange])

  /** «Вернуть» заново применяет правку — отложенных правок в системе нет. */
  const restore = useCallback(async (change: Change) => {
    setUndone(null)
    const brandId = change.key.brandId ?? change.brandId
    const channel = change.key.channelCode
    const field = change.fieldCode
    if (!field) return

    if (change.entityType === 'brand_channel' && brandId !== null && channel) {
      const patch = channelPatch(field, change.newValue)
      if (patch) await editChannel(brandId, channel, patch)
      return
    }
    if (change.entityType === 'brand_category_channel' && brandId !== null && channel
        && change.key.brandCategoryId !== undefined) {
      if (field === 'tier') {
        await editCategoryChannel(brandId, change.key.brandCategoryId, channel, { tier: asText(change.newValue) })
      } else if (field === 'isActive') {
        await editCategoryChannel(brandId, change.key.brandCategoryId, channel, { isActive: change.newValue === true })
      }
      return
    }
    if (change.entityType === 'brand' && brandId !== null) {
      if (field === 'name') {
        const name = asText(change.newValue)
        if (name !== null) await editBrand(brandId, { name })
      } else if (field === 'categoriesUnclear') {
        await editBrand(brandId, { categoriesUnclear: change.newValue === true })
      }
    }
  }, [editBrand, editCategoryChannel, editChannel])

  // ── публикация (§6.5) ─────────────────────────────────────────────────────

  const publish = useCallback(async () => {
    if (publishing || changes.length === 0 || unresolved > 0) return
    setPublishing(true)
    const resolutions = [...conflictIds]
      .map((changeId) => ({ changeId, decision: decisions[changeId] }))
      .filter((item): item is { changeId: number; decision: ConflictDecision } => item.decision !== undefined)
    const brandIds = [...new Set(changes.map((c) => c.brandId).filter((id): id is number => id !== null))]

    try {
      const res = await api.commit({ note: noteDraft.trim(), resolutions })
      await Promise.all([reloadBrands(), reloadDraft()])
      useApp.setState({ conflicts: [] })
      setDecisions({})
      setUndone(null)
      noteTouched.current = false

      if (res.appliedCount === 0) {
        toast('info', draftTexts.toast.nothingApplied)
      } else if (res.telegram === 'failed') {
        toast('success', draftTexts.toast.publishedNoTelegram(res.changeSetId))
      } else {
        toast('success', draftTexts.toast.published(
          res.changeSetId, res.appliedCount, res.telegram === 'sent' || res.telegram === 'queued',
        ))
      }

      const detail: PublishedEventDetail = {
        changeSetId: res.changeSetId,
        appliedCount: res.appliedCount,
        brandIds,
      }
      window.dispatchEvent(new CustomEvent<PublishedEventDetail>('bm:published', { detail }))
      leave()
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'conflict') {
        useApp.setState({ conflicts: err.conflicts })
        toast('error', draftTexts.toast.conflicts(err.conflicts.length))
      } else {
        const message = err instanceof Error ? err.message : texts.error.unknown
        toast('error', draftTexts.toast.publishFailed(message))
      }
    } finally {
      setPublishing(false)
    }
  }, [changes, conflictIds, decisions, navigate, noteDraft, publishing, reloadBrands, reloadDraft, toast, unresolved])

  // ── клавиатура: j/k по правкам, x — убрать, Ctrl+Enter — опубликовать ─────

  const publishRef = useRef(publish)
  publishRef.current = publish
  /* Модалка отмены — верхний слой: публиковать из-под неё нельзя. */
  const modalRef = useRef(discarding)
  modalRef.current = discarding

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target
      const typing = target instanceof HTMLElement
        && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        if (modalRef.current) return
        event.preventDefault()
        void publishRef.current()
        return
      }
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return

      const lines = [...(listRef.current?.querySelectorAll<HTMLElement>('[data-change-line]') ?? [])]
      if (lines.length === 0) return
      const active = document.activeElement
      const current = active instanceof HTMLElement
        ? lines.findIndex((line) => line === active || line.contains(active))
        : -1

      if (event.code === 'KeyJ') {
        event.preventDefault()
        lines[Math.min(current + 1, lines.length - 1)]?.focus()
      } else if (event.code === 'KeyK') {
        event.preventDefault()
        lines[Math.max(current - 1, 0)]?.focus()
      } else if (event.code === 'KeyX' && current >= 0) {
        event.preventDefault()
        lines[current]?.querySelector<HTMLElement>('[data-change-remove]')?.click()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  // ── пустой черновик (§12.8) ───────────────────────────────────────────────

  if (changes.length === 0) {
    return (
      <div className={embedded ? 'review review--embedded' : 'review'}>
        {embedded ? null : <h1 className="t-h1">{texts.screen.draft.title}</h1>}
        <EmptyState
          title={draftTexts.review.emptyTitle}
          text={draftTexts.review.emptyText}
          actions={(
            <Button variant="secondary" onClick={leave}>
              {embedded ? texts.action.close : texts.action.backToTable}
            </Button>
          )}
        />
      </div>
    )
  }

  const blocked = unresolved > 0
  const consequence = blocked ? texts.action.commitBlocked(unresolved) : texts.action.commitHint

  return (
    <div className={embedded ? 'review review--embedded' : 'review'}>
      {embedded ? null : (
        <>
          <Link className="review__back t-cell" to="/brands">
            <span className="review__back-icon" aria-hidden="true"><IconArrowLeft /></span>
            {texts.action.backToTable}
          </Link>

          <h1 className="review__title t-h1">{texts.screen.draft.title}</h1>
        </>
      )}
      <p className="review__meta t-meta">{subtitle}</p>

      {age > STALE_DAYS ? (
        <p className="review__stale t-cell" role="status">
          <span className="review__stale-icon" aria-hidden="true"><IconWarning /></span>
          {draftTexts.review.staleDraft(age)}
        </p>
      ) : null}

      <ConflictResolver
        conflicts={conflicts}
        changesById={changesById}
        ctx={ctx}
        users={bootstrap?.users ?? []}
        decisions={decisions}
        onDecide={(changeId, decision) => setDecisions((prev) => ({ ...prev, [changeId]: decision }))}
        onDecideAll={(decision) => setDecisions(() => {
          const next: ConflictDecisions = {}
          for (const id of conflictIds) next[id] = decision
          return next
        })}
      />

      {undone ? (
        <div className="review__undo" role="status">
          <span className="t-cell">
            {`${draftTexts.review.removedPrefix} ${brandNameOf(undone, ctx)} · ${changeTitle(undone, ctx)}`}
          </span>
          <Button size="s" variant="ghost" onClick={() => void restore(undone)}>
            {texts.action.undo}
          </Button>
        </div>
      ) : null}

      <div className="review__list" ref={listRef} aria-label={draftTexts.review.linesAria}>
        {groups.map((group) => (
          <ChangeGroup
            key={group.id}
            title={group.title}
            count={group.count}
            countInTitle={group.countInTitle}
          >
            {group.lines.map((line) => {
              if (line.kind === 'operation') {
                return (
                  <OperationLine
                    key={line.id}
                    title={line.title}
                    changes={line.changes}
                    ctx={ctx}
                    onRemoveAll={() => void removeChanges(line.dropIds, null)}
                    onRemoveOne={(change) => void removeChanges([change.id], change)}
                  />
                )
              }
              if (line.kind === 'task-done') {
                return (
                  <ChangeLine
                    key={line.id}
                    title={line.title}
                    to={line.report}
                    conflict={conflictIds.has(line.change.id)}
                    onRemove={() => void removeChanges(line.dropIds, null)}
                    removeLabel={draftTexts.review.removeAria(
                      `${brandNameOf(line.change, ctx)} · ${line.title}`,
                    )}
                  />
                )
              }
              if (line.kind === 'brand-create') {
                return (
                  <ChangeLine
                    key={line.id}
                    title={line.title}
                    to={line.summary}
                    onRemove={() => void removeChanges(line.dropIds, null)}
                  />
                )
              }
              const change = line.change
              return (
                <ChangeLine
                  key={line.id}
                  title={changeTitle(change, ctx)}
                  from={isValuePair(change) ? valueText(change, 'old') : null}
                  to={valueText(change, 'new')}
                  conflict={conflictIds.has(change.id)}
                  onRemove={() => void removeChanges([change.id], change)}
                  removeLabel={draftTexts.review.removeAria(
                    `${brandNameOf(change, ctx)} · ${changeTitle(change, ctx)}`,
                  )}
                />
              )
            })}
          </ChangeGroup>
        ))}
      </div>

      <p className="review__keys t-meta">{draftTexts.review.keys}</p>

      <div className="review__note">
        <label className="review__note-label t-label" htmlFor="review-note">
          {draftTexts.review.noteLabel}
        </label>
        <Textarea
          id="review-note"
          rows={2}
          value={noteDraft}
          onChange={(event) => { noteTouched.current = true; setNoteDraft(event.target.value) }}
          onBlur={() => setDraftNote(noteDraft)}
        />
      </div>

      <TelegramPreview
        changes={changes}
        note={noteDraft}
        enabled={bootstrap?.settings.telegramEnabled ?? false}
      />

      {/* Подвал закреплён внизу окна: список правок бывает на два экрана, и
          искать кнопку публикации прокруткой — лишняя работа. Обе кнопки
          одного роста и на одной линии: это два исхода одного решения,
          а не главное действие и приписка в углу. */}
      <div className="review__actions">
        {/* §6.4: причина блокировки — спокойная строка над кнопками, не красный текст. */}
        <ConsequenceLine id="review-consequence">{consequence}</ConsequenceLine>
        <div className="review__actions-row">
          <Button variant="danger-ghost" size="l" onClick={() => setDiscarding(true)}>
            {texts.action.draftCancelAll}
          </Button>
          <Button
            variant="primary"
            size="l"
            loading={publishing}
            disabled={blocked}
            aria-describedby="review-consequence"
            onClick={() => void publish()}
          >
            {texts.action.commitFinal(changes.length)}
          </Button>
        </div>
      </div>

      <DiscardDraftModal
        open={discarding}
        count={changes.length}
        onClose={() => setDiscarding(false)}
      />
    </div>
  )
}

// ───────────────────────────── возврат правки ────────────────────────────────

function asText(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  return String(value)
}

/**
 * Патч канала по коду поля. Разбор явный, без приведения типов: контракт
 * `BrandChannelPatch` должен проверяться компилятором, а не комментарием.
 */
function channelPatch(field: string, value: unknown): Parameters<typeof api.updateChannel>[2] | null {
  switch (field) {
    case 'isPresent': return { isPresent: value === true }
    case 'visibility': return { visibility: asText(value) }
    case 'tier': return { tier: asText(value) }
    case 'url': return { url: asText(value) }
    case 'stockDisplay': return { stockDisplay: asText(value) }
    case 'oos': return { oos: asText(value) }
    case 'delivery': return { delivery: asText(value) }
    case 'installation': return { installation: asText(value) }
    case 'warranty': return { warranty: asText(value) }
    case 'seoPriority': return { seoPriority: asText(value) }
    case 'contextPriority': return { contextPriority: asText(value) }
    default: return null
  }
}
